<template>
    <div
        class="flex items-center justify-end mt-1 text-sm font-medium text-red-500"
        data-testid="failure-indicator"
    >
        <template v-if="props.errorCode === SmsErrorCode.NoAccess">
            <activix-tooltip :content="t('smsCommunication.message.errorNoAccess')">
                <div class="flex items-center gap-1">
                    <icon name="regular/alert-circle" class="size-4" />
                    {{ t('smsCommunication.message.notDeliveredNoAccess') }}
                </div>
            </activix-tooltip>
        </template>

        <template v-else-if="props.errorCode === SmsErrorCode.Landline">
            <div class="flex items-center gap-1">
                {{ t('smsCommunication.message.notDeliveredUnreachable') }}
            </div>
        </template>

        <template v-else-if="props.errorCode === SmsErrorCode.Unreachable">
            <button
                class="flex items-center gap-1 hover:underline disabled:cursor-not-allowed disabled:opacity-60 disabled:no-underline"
                :disabled="props.isRetrying"
                @click="emit('retry')"
                v-if="!props.isReadOnly"
            >
                {{ t('smsCommunication.message.notDeliveredUnreachableWithRetry') }}
                <icon name="regular/redo" class="size-3.5" />
            </button>
            <div class="flex items-center gap-1" v-else>
                {{ t('smsCommunication.message.notDeliveredUnreachable') }}
            </div>
        </template>

        <template v-else>
            <activix-tooltip
                :content="t('smsCommunication.message.error')"
                v-if="isRetryable && !props.isReadOnly"
            >
                <button
                    class="flex items-center gap-1 hover:underline disabled:cursor-not-allowed disabled:opacity-60 disabled:no-underline"
                    :disabled="props.isRetrying"
                    @click="emit('retry')"
                >
                    <icon name="regular/alert-circle" class="size-4" />
                    {{ t('smsCommunication.message.notDeliveredWithRetry') }}
                    <icon name="regular/redo" class="size-3.5" />
                </button>
            </activix-tooltip>
            <div class="flex items-center gap-1" v-else>
                <icon name="regular/alert-circle" class="size-4" />
                {{ t('smsCommunication.message.notDelivered') }}
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue';

    import { useI18n } from '@/hooks/useI18n.ts';
    import { SmsErrorCode, NON_RETRYABLE_ERROR_CODES } from '@/entities/enums/SmsErrorCode.ts';

    interface Props {
        errorCode?: string | null;
        isReadOnly: boolean;
        isRetrying?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        errorCode: null,
        isRetrying: false,
    });
    const emit = defineEmits<(e: 'retry') => void>();

    const { t } = useI18n();

    const isRetryable = computed<boolean>(() => {
        if (!props.errorCode) {
            return true;
        }

        return !NON_RETRYABLE_ERROR_CODES.has(props.errorCode);
    });
</script>
